// Data_Privacy.js
import React from 'react';
import { FaShieldAlt, FaUserLock, FaCheckCircle, FaBook, FaDatabase, FaUserCheck, FaQuestionCircle, FaBalanceScale, FaGlobe, FaUserShield, FaClipboardCheck } from 'react-icons/fa';

const DataPrivacy = () => {
  // Smooth scroll function with offset
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80; // Adjust this based on your navbar height (e.g., 80px for the navbar)
      const sidebarHeight = 60; // Adjust this based on the sticky sidebar's top padding or height
      const offset = navbarHeight + sidebarHeight; // Total offset to account for fixed elements
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 select-none pt-20 pb-24">
      <div className="container mx-auto px-4 py-12 flex flex-col lg:flex-row gap-20">
        {/* Sticky Sidebar Navigation */}
        <aside className="lg:w-1/4 lg:sticky lg:top-20 lg:h-screen">
          <nav className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-maroon mb-4">Sections</h2>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={() => scrollToSection('security')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaShieldAlt /> Security
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('restricting-access')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaUserLock /> Restricting Access
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('accuracy')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaCheckCircle /> Accuracy
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('education')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaBook /> Education
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('reputable-sources')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaDatabase /> Reputable Sources
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('choice')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaUserCheck /> Choice
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('access-correction')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaQuestionCircle /> Access & Correction
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('accountability')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaBalanceScale /> Accountability
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('online-privacy')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaGlobe /> Online Privacy
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('identity-theft')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaUserShield /> Identity Theft
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('compliance')}
                  className="flex items-center gap-2 text-maroon hover:text-maroon-dark transition-all duration-300 hover:scale-105"
                >
                  <FaClipboardCheck /> Compliance
                </button>
              </li>
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="lg:w-3/4 bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Header */}
          <header className="bg-maroon text-white py-8 px-6 rounded-t-lg shadow-lg animate-fade-in-down">
            <h1 className="text-4xl font-bold tracking-tight">
              Data Privacy Principles
            </h1>
            <p className="mt-2 text-lg opacity-90">
              Criterionscreeners | Last Updated: April 01, 2025
            </p>
          </header>

          {/* Content Sections */}
          <section className="p-8 space-y-8 text-gray-700 animate-fade-in-up">
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                The Criterionscreeners Data Privacy Principles address the handling of personally identifiable information (PII), including sensitive PII, that is collected, maintained, used, or shared in relation to the services provided by Criterionscreeners for both our global and U.S. domestic products and services, where applicable.
              </p>
            </div>

            <div id="security">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaShieldAlt /> Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners safeguards the PII & SPII we maintain or disseminate by implementing appropriate administrative, physical, and technical measures to ensure it is not accessed by unauthorized individuals or used improperly.
              </p>
            </div>

            <div id="restricting-access">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaUserLock /> Restricting Access of Personally Identifiable Information
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners limits PII & SPII distribution based on its sensitivity, granting access to non-public PII only to authorized users or customers. Additional safeguards for sensitive information, such as Social Security numbers and driver’s license numbers are implemented.
              </p>
            </div>

            <div id="accuracy">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaCheckCircle /> Accuracy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners is committed to accurately reporting information in its products and ensuring the accuracy of the data received from its sources.
              </p>
            </div>

            <div id="education">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaBook /> Education
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners regularly educates its employees, users, and the public about the proper use of its products and services.
              </p>
            </div>

            <div id="reputable-sources">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaDatabase /> Reputable Sources
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners obtains PII from established, reputable sources within both the government and private sectors. To uphold this principle, Criterionscreeners takes reasonable measures to evaluate the reputation and reliability of its private sector data sources before integrating PII from these sources into its products and services.
              </p>
            </div>

            <div id="choice">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaUserCheck /> Choice
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners provides customers with the option to opt out of the dissemination of PII from certain Criterionscreeners-owned databases that are used exclusively for marketing our services.
              </p>
            </div>

            <div id="access-correction">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaQuestionCircle /> Access & Correction
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners offers consumers a central point of contact for any questions they may have about the company and its commitment to the responsible use of PII.
              </p>
            </div>

            <div id="accountability">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaBalanceScale /> Accountability
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners advocates for accountability in industry standards, effective federal regulation, and legislation for data providers. The company supports industry oversight, privacy community engagement, and believes strong privacy and security protections are essential for a trusted data industry.
              </p>
            </div>

            <div id="online-privacy">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaGlobe /> Online Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners safeguards the privacy of PII obtained online by applying our Data Privacy Principles and evolving standards to the digital environment.
              </p>
            </div>

            <div id="identity-theft">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaUserShield /> Identity Theft
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners is committed to preventing the misuse of information from its products and services, including for purposes like identity theft.
              </p>
            </div>

            <div id="compliance">
              <h2 className="text-2xl font-semibold text-maroon mb-4 flex items-center gap-2">
                <FaClipboardCheck /> Compliance
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Criterionscreeners obtains assessments from a qualified, objective, independent third party to assess administrative, technical, and physical safeguards, as appropriate.
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default DataPrivacy;